import React, { memo, ReactNode, useMemo } from "react";
import { Checkbox, Typography } from "antd";
import "./styles.css";
import QueryBodyItem from "./QueryBodyItem";

const CheckTypeMap = {
  UN_CHECKED: 0,
  CHECKED: 1,
  PARTIALLY_CHECKED: 2,
};

const defaultConfig = {
  titleCheckbox: true,
  columnCheckbox: true,
}

const QueryNode = ({
  tableName,
  columns,
  isTableChecked,
  checkedColumns = [],
  onTitleCheckChange,
  onBodyCheckChange,
  classes,
  titleActions,
  columnStyleFn,
  showColumnExtra = false,
  columnExtraFn,
  config,
  hideUnselectedCheckbox = false,
  listRef,
}: {
  tableName: string;
  columns: string[];
  isTableChecked: boolean;
  checkedColumns: string[];
  onTitleCheckChange: (tableName: string, value: boolean) => void;
  onBodyCheckChange: (columnName: string, tableName: string, value: boolean) => void;
  classes?: {
    nodeWrapper?: string;
    head?: string;
    headCheck?: string;
    column?: string;
    columnCheck?: string;
  };
  titleActions?: ReactNode | any[];
  columnStyleFn?: (column: string, tableName: string, idx: number) => string;
  showColumnExtra?: boolean;
  columnExtraFn?: (columnName: string, tableName: string, index: number) => React.ReactNode;
  config?: { titleCheckbox?: boolean, columnCheckbox?: boolean };
  hideUnselectedCheckbox?: boolean;
  listRef?: React.MutableRefObject<(HTMLLIElement | null)[]>;
}) => {
  const updatedConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);
  let checkType = CheckTypeMap.UN_CHECKED; // No Columns checked
  // if (isTableChecked && checkedColumns?.length !== columns.length)
  //   checkType = CheckTypeMap.PARTIALLY_CHECKED; // Partially checked
  if (isTableChecked)
    checkType = CheckTypeMap.CHECKED; // All checked

  const isFullyChecked = checkType === CheckTypeMap.CHECKED;
  const isPartiallyChecked = checkType === CheckTypeMap.PARTIALLY_CHECKED;

  return (
    <div className={`query-node-wrapper ${classes?.nodeWrapper}`}>
      <div
        className={`query-node-head ${isFullyChecked ? "head-fully-checked" : ""} ${isPartiallyChecked ? "head-partially-checked" : ""} ${classes?.head}`}
      >
        <Typography.Text
          className={`query-node-title ${isFullyChecked ? "title-fully-checked" : ""} ${isPartiallyChecked ? "title-partially-checked" : ""}`}
          ellipsis={{ tooltip: tableName }}
        >
          {tableName}
        </Typography.Text>
        {titleActions}
        <Checkbox
          className={`query-node-head-checkbox ${isFullyChecked ? "head-checkbox-fully-checked" : ""} ${updatedConfig.titleCheckbox ? "visible-checkbox" : "hidden-checkbox"} ${classes?.headCheck}`}
          onChange={(e) => {
            if (!hideUnselectedCheckbox) {
              onTitleCheckChange(tableName, e.target.checked);
            }
          }}
          checked={isFullyChecked}
          disabled={hideUnselectedCheckbox}
          indeterminate={isPartiallyChecked}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!hideUnselectedCheckbox) {
                onTitleCheckChange(tableName, !isFullyChecked);
              }
            }
          }}
          title={isFullyChecked ? 'Deselect Table & All columns' : isPartiallyChecked ? 'Select All columns' : 'Select Table & All columns'}
        />
      </div>
      <div className={`query-node-body-wrapper nowheel ${(isTableChecked || isPartiallyChecked) ? 'table-selected' : ''} ${classes?.column}`}>
        <ul className="query-node-body">
          {columns?.map((columnName: string, index: number) => {
            const isChecked = checkedColumns?.includes(columnName);
            const customColStyle = columnStyleFn ? columnStyleFn(columnName, tableName, index) : ''

            return (
              <QueryBodyItem
                key={columnName}
                columnName={columnName}
                tableName={tableName}
                isChecked={isChecked}
                onBodyCheckChange={onBodyCheckChange}
                showColumnExtra={showColumnExtra}
                columnExtraFn={columnExtraFn}
                customColStyle={customColStyle}
                index={index}
                classes={classes}
                config={updatedConfig}
                listRef={listRef}
                hideUnselectedCheckbox={hideUnselectedCheckbox}
              />
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default memo(QueryNode);
